import { useState, useEffect, useCallback, type FormEvent } from 'react';

import './FleetManagement.css';
import { useTranslation } from 'react-i18next';
import { fetchStaffList, mapStaffToUi, type MappedStaff } from '../../services/staffApi';
import { saveFleetInfo, updateFleetInfo, fetchVehicles, type Vehicle } from '../../services/transportFleetApi';

function formatTimestamp(iso: string): string {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Returns 'expired' | 'warning' | 'valid' based on date compliance */
function getComplianceStatus(dateStr: string): 'expired' | 'warning' | 'valid' {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const expiry = new Date(dateStr + 'T00:00:00');
    if (expiry < today) return 'expired';
    const diff = (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    return diff <= 30 ? 'warning' : 'valid';
}

type DrawerMode = null | 'create' | 'edit';
interface FormErrors { vehicle_number?: string; model?: string; driver_name?: string; }

export default function FleetManagement() {
    const { t: _t } = useTranslation();
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [search, setSearch] = useState('');

    const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formVehicleNo, setFormVehicleNo] = useState('');
    const [formModel, setFormModel] = useState('');
    const [formCapacity, setFormCapacity] = useState(40);
    const [formDriver, setFormDriver] = useState('');
    const [formDriverPhone, setFormDriverPhone] = useState('');
    const [formInsurance, setFormInsurance] = useState('');
    const [formFitness, setFormFitness] = useState('');
    const [formIsActive, setFormIsActive] = useState(true);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editCreatedAt, setEditCreatedAt] = useState('');
    const [editUpdatedAt, setEditUpdatedAt] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<Vehicle | null>(null);
    const [staff, setStaff] = useState<MappedStaff[]>([])

    useEffect(() => {
        (async () => { setVehicles(await fetchVehicles()); setLoading(false); })();
    }, []);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            fetchStaffList({ isTeacher: false }).then((res) => {
                if (!isMounted) {
                    return;
                }
                setStaff(res.data.map(mapStaffToUi).filter(staff => staff.designation === 'Driver'));
            })
        })()
        return () => {
            isMounted = false;
        }
    }, [])

    const filtered = vehicles.filter((v) => {
        const q = search.toLowerCase();
        const driverName = v.driver ? `${v.driver.first_name} ${v.driver.last_name}` : '';
        return v.vehicle_number.toLowerCase().includes(q) || (v.model ?? '').toLowerCase().includes(q) || driverName.toLowerCase().includes(q);
    });

    const resetForm = useCallback(() => {
        setFormVehicleNo(''); setFormModel(''); setFormCapacity(40); setFormDriver('');
        setFormDriverPhone(''); setFormInsurance(''); setFormFitness('');
        setFormIsActive(true); setErrors({}); setEditingId(null);
        setEditCreatedAt(''); setEditUpdatedAt('');
    }, []);

    const openCreate = () => { resetForm(); setDrawerMode('create'); };
    const openEdit = (v: Vehicle) => {
        resetForm();
        setFormVehicleNo(v.vehicle_number); setFormModel(v.model ?? ''); setFormCapacity(v.seating_capacity);
        setFormDriver(v.driver_id ?? ''); setFormDriverPhone(v.contact_number ?? '');
        setFormInsurance(v.insurance_expiry ?? ''); setFormFitness(v.fitness_expiry ?? '');
        setFormIsActive(v.is_active); setEditingId(v.id);
        setEditCreatedAt(v.created_at); setEditUpdatedAt(v.updated_at);
        setDrawerMode('edit');
    };
    const closeDrawer = () => { setDrawerMode(null); resetForm(); };

    const validate = (): boolean => {
        const e: FormErrors = {};
        if (!formVehicleNo.trim()) e.vehicle_number = 'Vehicle number is required.';
        if (!(formModel ?? '').trim()) e.model = 'Model is required.';
        if (!(formDriver ?? '').trim()) e.driver_name = 'Driver name is required.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        if (!validate()) return;
        setIsSubmitting(true);
        const payload = {
            vehicle_number: formVehicleNo.trim(),
            model: formModel.trim(),
            seating_capacity: formCapacity,
            driver_id: formDriver?.trim() || null,
            contact_number: formDriverPhone?.trim() || null,
            insurance_expiry: formInsurance,
            fitness_expiry: formFitness,
            is_active: formIsActive,
        };
        try {
            if (drawerMode === 'edit' && editingId) {
                await updateFleetInfo(editingId, payload);
                setVehicles((prev) => prev.map((v) => v.id === editingId ? { ...v, ...payload, updated_at: new Date().toISOString() } : v));
                setSuccessMsg('Vehicle updated successfully!');
            } else {
                await saveFleetInfo(payload);
                setVehicles((prev) => [...prev, { id: String(Date.now()), ...payload, created_at: new Date().toISOString(), updated_at: new Date().toISOString() } as Vehicle]);
                setSuccessMsg('Vehicle added successfully!');
            }
            closeDrawer();
            setTimeout(() => setSuccessMsg(''), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        setVehicles((prev) => prev.filter((v) => v.id !== deleteTarget.id));
        setDeleteTarget(null);
        setSuccessMsg('Vehicle removed!');
        setTimeout(() => setSuccessMsg(''), 5000);
    };

    if (loading) {
        return <div className="fleet"><div className="fleet__loading"><div className="fleet__loader"><span className="fleet__loader-dot" /><span className="fleet__loader-dot" /><span className="fleet__loader-dot" /></div></div></div>;
    }

    return (
        <div className="fleet">
            {successMsg && (
                <div className="fleet__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="fleet__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            <div className="fleet__header">
                <div className="fleet__header-text">
                    <h1 className="fleet__title">Fleet Management</h1>
                    <p className="fleet__subtitle">Manage vehicles, drivers, and compliance tracking.</p>
                </div>
                <div className="fleet__header-actions">
                    <div className="fleet__search-wrap">
                        <svg className="fleet__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        <input className="fleet__search" type="text" placeholder="Search vehicles…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <button className="fleet__add-btn" onClick={openCreate}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        Add Vehicle
                    </button>
                </div>
            </div>

            <div className="fleet__table-wrap">
                <table className="fleet__table">
                    <thead>
                        <tr>
                            <th className="fleet__th">Vehicle No.</th>
                            <th className="fleet__th">Model</th>
                            <th className="fleet__th fleet__th--center">Capacity</th>
                            <th className="fleet__th">Driver</th>
                            <th className="fleet__th">Insurance Exp.</th>
                            <th className="fleet__th">Fitness Exp.</th>
                            <th className="fleet__th fleet__th--center">Status</th>
                            <th className="fleet__th fleet__th--actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((v) => {
                            const insStatus = v.insurance_expiry ? getComplianceStatus(v.insurance_expiry) : 'valid';
                            const fitStatus = v.fitness_expiry ? getComplianceStatus(v.fitness_expiry) : 'valid';
                            const driverDisplay = v.driver ? `${v.driver.first_name} ${v.driver.last_name}` : '—';
                            return (
                                <tr className="fleet__row" key={v.id}>
                                    <td className="fleet__td fleet__td--vno">{v.vehicle_number}</td>
                                    <td className="fleet__td">{v.model}</td>
                                    <td className="fleet__td fleet__td--center fleet__td--capacity">{v.seating_capacity}</td>
                                    <td className="fleet__td fleet__td--driver">{driverDisplay}</td>
                                    <td className="fleet__td">
                                        <span className={`fleet__compliance fleet__compliance--${insStatus}`}>
                                            {insStatus === 'expired' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>}
                                            {insStatus === 'warning' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
                                            {v.insurance_expiry ? formatDate(v.insurance_expiry) : '—'}
                                        </span>
                                    </td>
                                    <td className="fleet__td">
                                        <span className={`fleet__compliance fleet__compliance--${fitStatus}`}>
                                            {fitStatus === 'expired' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>}
                                            {fitStatus === 'warning' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
                                            {v.fitness_expiry ? formatDate(v.fitness_expiry) : '—'}
                                        </span>
                                    </td>
                                    <td className="fleet__td fleet__td--center">
                                        <span className={`fleet__status ${v.is_active ? 'fleet__status--active' : 'fleet__status--maintenance'}`}>
                                            {v.is_active ? 'Running' : 'Maintenance'}
                                        </span>
                                    </td>
                                    <td className="fleet__td fleet__td--actions">
                                        <button className="fleet__row-btn" onClick={() => openEdit(v)}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                            Edit
                                        </button>
                                        <button className="fleet__row-btn fleet__row-btn--delete" onClick={() => setDeleteTarget(v)}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Drawer */}
            {drawerMode && (
                <>
                    <div className="fleet__overlay" onClick={closeDrawer} />
                    <aside className="fleet__drawer">
                        <div className="fleet__drawer-header">
                            <h2 className="fleet__drawer-title">{drawerMode === 'edit' ? 'Edit Vehicle' : 'Add Vehicle'}</h2>
                            <button className="fleet__drawer-close" onClick={closeDrawer} title="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                        </div>
                        <form className="fleet__drawer-body" onSubmit={handleSubmit} noValidate id="fleet-form">
                            <div className="fleet__card">
                                <h3 className="fleet__card-title">Vehicle Details</h3>
                                <div className="fleet__form-row">
                                    <div className="fleet__field">
                                        <label className="fleet__label fleet__label--required" htmlFor="fVNo">Vehicle Number</label>
                                        <input className={`fleet__input ${errors.vehicle_number ? 'fleet__input--error' : ''}`} type="text" id="fVNo" placeholder="e.g. KA-01-AB-1234" value={formVehicleNo} onChange={(e) => { setFormVehicleNo(e.target.value); if (errors.vehicle_number) setErrors((p) => ({ ...p, vehicle_number: undefined })); }} autoFocus />
                                        {errors.vehicle_number && <p className="fleet__error">{errors.vehicle_number}</p>}
                                    </div>
                                    <div className="fleet__field">
                                        <label className="fleet__label fleet__label--required" htmlFor="fModel">Model</label>
                                        <input className={`fleet__input ${errors.model ? 'fleet__input--error' : ''}`} type="text" id="fModel" placeholder="e.g. Tata Starbus" value={formModel} onChange={(e) => { setFormModel(e.target.value); if (errors.model) setErrors((p) => ({ ...p, model: undefined })); }} />
                                        {errors.model && <p className="fleet__error">{errors.model}</p>}
                                    </div>
                                </div>
                                <div className="fleet__field">
                                    <label className="fleet__label" htmlFor="fCap">Seating Capacity</label>
                                    <input className="fleet__input" type="number" id="fCap" min={1} max={100} value={formCapacity} onChange={(e) => setFormCapacity(Number(e.target.value))} />
                                </div>
                            </div>
                            <div className="fleet__card">
                                <h3 className="fleet__card-title">Driver Information</h3>
                                <div className="fleet__form-row">
                                    <div className="fleet__field">
                                        <label className="fleet__label fleet__label--required" htmlFor="fDrv">Driver Name</label>
                                        <select
                                            className={`fleet__input ${errors.driver_name ? 'fleet__input--error' : ''}`} id="fDrv" value={formDriver}
                                            onChange={(e) => {
                                                setFormDriver(e.target.value);
                                                if (errors.driver_name) setErrors((p) => ({ ...p, driver_name: undefined }));
                                            }} >
                                            <option value="">Select driver</option>
                                            {staff.map(driver => (
                                                <option key={driver.user_id} value={driver.user_id}>{driver.first_name} {driver.last_name}</option>
                                            ))}
                                        </select>
                                        {errors.driver_name && <p className="fleet__error">{errors.driver_name}</p>}
                                    </div>
                                    <div className="fleet__field">
                                        <label className="fleet__label" htmlFor="fDrvPh">Phone</label>
                                        <input className="fleet__input" type="tel" id="fDrvPh" placeholder="+91…" value={formDriverPhone} onChange={(e) => setFormDriverPhone(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            <div className="fleet__card">
                                <h3 className="fleet__card-title">Compliance</h3>
                                <div className="fleet__form-row">
                                    <div className="fleet__field">
                                        <label className="fleet__label" htmlFor="fIns">Insurance Expiry</label>
                                        <input className="fleet__input" type="date" id="fIns" value={formInsurance} onChange={(e) => setFormInsurance(e.target.value)} />
                                    </div>
                                    <div className="fleet__field">
                                        <label className="fleet__label" htmlFor="fFit">Fitness Expiry</label>
                                        <input className="fleet__input" type="date" id="fFit" value={formFitness} onChange={(e) => setFormFitness(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            <div className="fleet__card">
                                <h3 className="fleet__card-title">Settings</h3>
                                <div className="fleet__toggle-row">
                                    <div className="fleet__toggle-info"><span className="fleet__toggle-label">Active / Running</span><span className="fleet__toggle-desc">Mark as under maintenance if inactive</span></div>
                                    <label className="fleet__toggle"><input type="checkbox" checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} /><span className="fleet__toggle-track" /></label>
                                </div>
                            </div>
                            {drawerMode === 'edit' && editCreatedAt && (
                                <div className="fleet__timestamps"><span className="fleet__timestamp">Created: {formatTimestamp(editCreatedAt)}</span><span className="fleet__timestamp">Updated: {formatTimestamp(editUpdatedAt)}</span></div>
                            )}
                        </form>
                        <div className="fleet__drawer-footer">
                            <button className="fleet__submit" type="submit" form="fleet-form" disabled={isSubmitting}>
                                {isSubmitting ? (<><svg className="fleet__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>{drawerMode === 'edit' ? 'Updating…' : 'Creating…'}</>) : drawerMode === 'edit' ? 'Update Vehicle' : 'Add Vehicle'}
                            </button>
                            <button className="fleet__cancel" type="button" onClick={closeDrawer} disabled={isSubmitting}>Cancel</button>
                        </div>
                    </aside>
                </>
            )}

            {deleteTarget && (
                <div className="fleet__confirm-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="fleet__confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="fleet__confirm-title">Remove Vehicle</h3>
                        <p className="fleet__confirm-text">Are you sure you want to remove <strong>{deleteTarget.vehicle_number}</strong>? This action can be undone by an administrator.</p>
                        <div className="fleet__confirm-actions">
                            <button className="fleet__confirm-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="fleet__confirm-delete" onClick={confirmDelete}>Remove</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
